import { useCallback } from 'react';
import { useAppSelector } from '../store';
import type { AppFieldKey } from '../config/fieldConfig';

type ConfigFields = Record<
  string,
  | {
      label?: string | null;
      labelValueSingular?: string | null;
      labelValuePlural?: string | null;
    }
  | undefined
>;

const normalizeConfigKey = (
  key: AppFieldKey | string | number | null | undefined
): string | null => {
  if (key === null || key === undefined || key === '') {
    return null;
  }

  return String(key)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

const compactConfigKey = (key: string): string =>
  key.replace(/_/g, '');

export const resolveFieldLabel = (
  fields: ConfigFields,
  fieldKey: AppFieldKey | string | number,
  defaultLabel: string,
  isPlural: boolean = false
): string => {
    
  const normalizedKey = normalizeConfigKey(fieldKey);
 
  if (!normalizedKey) {
    return defaultLabel;
  }

  const field =
    fields[normalizedKey] ||
    fields[compactConfigKey(normalizedKey)];

  if (!field) {
    return defaultLabel;
  }

  const label = isPlural
    ? field.labelValuePlural ||
      field.labelValueSingular 
     
    : field.labelValueSingular ||
      field.labelValuePlural 
      ;

    

  return label?.trim() || defaultLabel;
};

export const useFieldLabel = (
  fieldKey: AppFieldKey | string | number,
  defaultLabel: string,
  isPlural: boolean = false
): string => {
  return useAppSelector((state) =>
    resolveFieldLabel(
      state.appConfig.fields,
      fieldKey,
      defaultLabel,
      isPlural
    )
  );
};

export const useFieldLabelResolver = () => {
  const fields = useAppSelector(
    (state) => state.appConfig.fields
  );

  return useCallback(
    (
      fieldKey: AppFieldKey | string | number,
      defaultLabel: string,
      isPlural: boolean = false
    ) =>
      resolveFieldLabel(
        fields,
        fieldKey,
        defaultLabel,
        isPlural
      ),
    [fields]
  );
};

export const useSingularFieldLabel = (
  fieldKey: AppFieldKey | string | number,
  defaultLabel: string
): string => {
  return useFieldLabel(fieldKey, defaultLabel, false);
};

export const usePluralFieldLabel = (
  fieldKey: AppFieldKey | string | number,
  defaultLabel: string
): string => {
  return useFieldLabel(fieldKey, defaultLabel, true);
};